// Copyright ©️ 2026 eVolve MEP, LLC

import { useMemo } from 'react';

import { workOrderStateNames, today, type DashboardFilterToApi } from '@evolvemep/api-ts';

import { BasePageHeader } from 'components/Mantine/BasePageHeader';
import { ChangeDataViewProvider } from 'components/Mantine/ChangeDataView/useChangeDataView';
import { useGlobalFilters } from 'components/Mantine/GlobalFilters/useGlobalFilters';
import { convertTimeFrameDataToRequestParams } from 'components/Mantine/TimeFramePicker/helpers';
import { InsightsChart } from 'modules/Shop/Insights/ShopInsights/InsightsChart';
import { InsightsDetailedReport } from 'modules/Shop/Insights/ShopInsights/InsightsDetailedReport';

const filterStates = workOrderStateNames.filter((n) => n !== 'Transferred' && n !== 'Canceled');

export const InsightsPage = () => {
  const { facilityIds, projectIds, workOrderStateIds, timeFrame } = useGlobalFilters({
    facilities: true,
    projects: true,
    workOrderStates: filterStates,
    timeFrame: {
      defaultTimeFrame: 'This Year',
    },
  });

  const params = useMemo<DashboardFilterToApi>(
    () => ({
      projects: projectIds.length === 0 ? ['All Projects'] : projectIds,
      facilities: facilityIds.length === 0 ? ['All Facilities'] : facilityIds,
      states: workOrderStateIds.length === 0 ? ['All States'] : workOrderStateIds,
      ...convertTimeFrameDataToRequestParams(timeFrame),
      currentDate: today(),
    }),
    [facilityIds, projectIds, timeFrame, workOrderStateIds],
  );

  return (
    <ChangeDataViewProvider name="Shop Insights" viewsToShow={['chart', 'table']}>
      {(selectedView) => (
        <>
          <BasePageHeader title="Shop Insights" />
          {selectedView === 'chart' ? <InsightsChart params={params} /> : <InsightsDetailedReport params={params} />}
        </>
      )}
    </ChangeDataViewProvider>
  );
};
